import apiClient from "./baseService";
import {handleAxiosError} from "./axiosErrorHandler";
import type {User, UserLimitedInfo} from "../types/domain/user";
import {Company} from "../types/domain/company";
import {CompanyMemberEx} from "../types/domain/companyMember";
import {UserNotificationsStats} from "../types/domain/userNotificationsStats";

export interface GetUserCompaniesQueryType {
    includeChildCompanies?: boolean;
}

export interface PostUserQueryType {
    suppressEmail?: boolean;
}

export type PostUserBodyType = Pick<User, "security" | "properties" | "preferences" | "isApiEnabled" | "isActive">;

export type PatchUserBodyType = Partial<
    Pick<User, "security" | "properties" | "preferences" | "isApiEnabled" | "isActive">
>;

export type PostUserResponseType = User & {password: string};

export const getUserById = async (userId: string): Promise<User> => {
    return await apiClient
        .get<User>(`/users/${userId}`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getUserLimitedInfoById = async (userId: string): Promise<UserLimitedInfo> => {
    return await apiClient
        .get<UserLimitedInfo>(`/users/${userId}/limited-info`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getUsers = async (): Promise<User[]> => {
    return await apiClient
        .get<User[]>("/users")
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getUserCompanies = async (userId: string, query: GetUserCompaniesQueryType): Promise<Company[]> => {
    return await apiClient
        .get<Company[]>(`/users/${userId}/companies`, {params: query})
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getUserMemberships = async (userId: string): Promise<CompanyMemberEx[]> => {
    return await apiClient
        .get<CompanyMemberEx[]>(`/users/${userId}/memberships`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const patchUser = async (id: string, body: PatchUserBodyType): Promise<User> => {
    return await apiClient
        .patch<User>(`/users/${id}`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const createUser = async (body: PostUserBodyType, query: PostUserQueryType): Promise<PostUserResponseType> => {
    return await apiClient
        .post<PostUserResponseType>("/users", body, {params: query})
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const deleteUser = async (userId: string) => {
    await apiClient.delete(`/users/${userId}`).catch(handleAxiosError);
};

export const getUserNotificationsStats = async (userId: string) => {
    return await apiClient
        .get<UserNotificationsStats>(`/users/${userId}/notifications-stats`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
